import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  Active,
  Announcements,
  closestCenter,
  CollisionDetection,
  DragOverlay,
  DndContext,
  DropAnimation,
  MouseSensor,
  TouchSensor,
  KeyboardCoordinateGetter,
  Modifiers,
  MeasuringConfiguration,
  PointerActivationConstraint,
  ScreenReaderInstructions,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects, KeyboardSensor, PointerSensor, UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  SortingStrategy,
  rectSortingStrategy,
  AnimateLayoutChanges,
  NewIndexGetter,
} from '@dnd-kit/sortable';
import { Item } from './Item';
import { List } from './List';
import { Wrapper } from './Wrapper';

const defaultInitializer = (index: number) => index;

export function createRange<T = number>(
  length: number,
  initializer: (index: number) => any = defaultInitializer,
): T[] {
  return [...new Array(length)].map((_, index) => initializer(index));
}

export interface Props {
  activationConstraint?: PointerActivationConstraint;
  animateLayoutChanges?: AnimateLayoutChanges;
  adjustScale?: boolean;
  collisionDetection?: CollisionDetection;
  columns?: number;
  coordinateGetter?: KeyboardCoordinateGetter;
  Container?: any; // To-do: Fix me
  dropAnimation?: DropAnimation | null;
  getNewIndex?: NewIndexGetter;
  handle?: boolean;
  items: React.ReactElement[];
  measuring?: MeasuringConfiguration;
  modifiers?: Modifiers;
  renderItem?: any;
  onItemsMove?: (array: React.ReactElement[], from: number, to: number) => void;
  onItemResize?: (id: UniqueIdentifier, width?: number) => void;
  onItemsRemove?: (id: UniqueIdentifier) => void;
  reorderItems?: typeof arrayMove;
  showHandle?: boolean;
  strategy?: SortingStrategy;
  style?: React.CSSProperties;
  useDragOverlay?: boolean;
  getItemStyles?(args: {
    id: UniqueIdentifier;
    index: number;
    isSorting: boolean;
    isDragOverlay: boolean;
    overIndex: number;
    isDragging: boolean;
  }): React.CSSProperties;
  wrapperStyle?(args: {
    active: Pick<Active, 'id'> | null;
    index: number;
    isDragging: boolean;
    id: UniqueIdentifier;
  }): React.CSSProperties;
  isDisabled?(id: UniqueIdentifier): boolean;
}

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

const screenReaderInstructions: ScreenReaderInstructions = {
  draggable: `
    To pick up a sortable item, press the space bar.
    While sorting, use the arrow keys to move the item.
    Press space again to drop the item in its new position, or press escape to cancel.
  `,
};

export function Sortable(props: Props) {
  const {
    activationConstraint,
    animateLayoutChanges,
    adjustScale = false,
    columns,
    Container = List,
    collisionDetection = closestCenter,
    coordinateGetter = sortableKeyboardCoordinates,
    dropAnimation = dropAnimationConfig,
    getItemStyles = () => ({}),
    getNewIndex,
    handle = false,
    items: initialItems,
    isDisabled = () => false,
    measuring,
    modifiers,
    onItemsMove,
    onItemResize,
    onItemsRemove,
    renderItem,
    reorderItems = arrayMove,
    showHandle,
    strategy = rectSortingStrategy,
    style,
    useDragOverlay = true,
    wrapperStyle = () => ({}),
  } = props;

  const [items, setItems] = useState<React.ReactElement[]>(initialItems);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint,
    }),
    useSensor(TouchSensor, {
      activationConstraint,
    }),
    useSensor(KeyboardSensor, {
      scrollBehavior: 'Cypress' in window ? 'auto' : undefined,
      coordinateGetter,
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );
  const isFirstAnnouncement = useRef(true);
  const getIndex = (id: UniqueIdentifier) => items.indexOf(id);
  const getPosition = (id: UniqueIdentifier) => getIndex(id) + 1;
  const activeIndex = activeId ? getIndex(activeId) : -1;

  const announcements: Announcements = {
    onDragStart({ active: { id } }) {
      return `Picked up sortable item ${String(
        id,
      )}. Sortable item ${id} is in position ${getPosition(id)} of ${
        items.length
      }`;
    },
    onDragOver({ active, over }) {
      if (isFirstAnnouncement.current === true) {
        isFirstAnnouncement.current = false;
        return;
      }

      if (over) {
        return `Sortable item ${
          active.id
        } was moved into position ${getPosition(over.id)} of ${items.length}`;
      }
    },
    onDragEnd({ active, over }) {
      if (over) {
        return `Sortable item ${
          active.id
        } was dropped at position ${getPosition(over.id)} of ${items.length}`;
      }
    },
    onDragCancel({ active: { id } }) {
      return `Sorting was cancelled. Sortable item ${id} was dropped and returned to position ${getPosition(
        id,
      )} of ${items.length}.`;
    },
  };

  useEffect(() => {
    if (!activeId) {
      isFirstAnnouncement.current = true;
    }
  }, [activeId]);

  return (
    <DndContext
      accessibility={{
        announcements,
        screenReaderInstructions,
      }}
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={({ active }) => {
        if (!active) {
          return;
        }

        setActiveId(active.id);
      }}
      onDragEnd={({ over }) => {
        setActiveId(null);

        if (over) {
          const overIndex = getIndex(over.id);
          if (activeIndex !== overIndex) {
            const orderedItems = reorderItems(items, activeIndex, overIndex);
            setItems((items) => orderedItems);

            if (onItemsMove) {
              onItemsMove(orderedItems, activeIndex, overIndex);
            }
          }
        }
      }}
      onDragCancel={() => setActiveId(null)}
      measuring={measuring}
      modifiers={modifiers}
    >
      <Wrapper style={style} center>
        <SortableContext items={items} strategy={strategy}>
          <Container columns={columns}>
            {items.map((value, index) => (
              <SortableItem
                animateLayoutChanges={animateLayoutChanges}
                disabled={isDisabled(value)}
                getNewIndex={getNewIndex}
                handle={handle}
                id={value}
                index={index}
                key={index}
                onRemove={onItemsRemove}
                onResize={onItemResize}
                renderItem={renderItem}
                showHandle={showHandle}
                style={getItemStyles}
                useDragOverlay={useDragOverlay}
                width={value.props.width}
                wrapperStyle={wrapperStyle}
              >
                {value}
              </SortableItem>
            ))}
          </Container>
        </SortableContext>
      </Wrapper>
      {useDragOverlay
        ? createPortal(
          <DragOverlay
            adjustScale={adjustScale}
            dropAnimation={dropAnimation}
          >
            {activeId ? (
              <Item
                width={items[activeIndex].props.width}
                value={items[activeIndex]}
                handle={handle}
                renderItem={renderItem}
                wrapperStyle={wrapperStyle({
                  active: { id: activeId },
                  index: activeIndex,
                  isDragging: true,
                  id: items[activeIndex],
                })}
                style={getItemStyles({
                  id: items[activeIndex],
                  index: activeIndex,
                  isSorting: activeId !== null,
                  isDragging: true,
                  overIndex: -1,
                  isDragOverlay: true,
                })}
                dragOverlay
                onResize={onItemResize}
              >
                {items[activeIndex]}
              </Item>
            ) : null}
          </DragOverlay>,
          document.body,
        )
        : null}
    </DndContext>
  );
}

interface SortableItemProps {
  animateLayoutChanges?: AnimateLayoutChanges;
  children: any;
  disabled?: boolean;
  getNewIndex?: NewIndexGetter;
  handle: boolean;
  id: UniqueIdentifier;
  index: number;
  onRemove?(id: UniqueIdentifier): void;
  onResize?(id: UniqueIdentifier, width?: number): void;
  renderItem?(args: any): React.ReactElement;
  showHandle?: boolean;
  style(values: any): React.CSSProperties;
  useDragOverlay?: boolean;
  width?: number;
  wrapperStyle: Props['wrapperStyle'];
}

export function SortableItem({
  children,
  disabled,
  animateLayoutChanges,
  getNewIndex,
  handle,
  id,
  index,
  onRemove,
  onResize,
  showHandle,
  style,
  renderItem,
  useDragOverlay,
  width,
  wrapperStyle,
}: SortableItemProps) {
  const {
    active,
    attributes,
    isDragging,
    isSorting,
    listeners,
    overIndex,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    animateLayoutChanges,
    disabled,
    getNewIndex,
  });

  return (
    <Item
      width={width || 1}
      ref={setNodeRef}
      disabled={disabled}
      dragging={isDragging}
      sorting={isSorting}
      handle={handle}
      handleProps={
          handle
            ? {
              ref: setActivatorNodeRef,
            }
            : undefined
        }
      renderItem={renderItem}
      index={index}
      style={style({
        index,
        id,
        isDragging,
        isSorting,
        overIndex,
      })}
      onRemove={onRemove ? () => onRemove(id) : undefined}
      onResize={onResize ? () => onResize(id, width === 1 ? 2 : 1) : undefined}
      transform={transform}
      transition={transition}
      wrapperStyle={wrapperStyle?.({
        index, isDragging, active, id,
      })}
      listeners={listeners}
      data-index={index}
      data-id={id}
      dragOverlay={!useDragOverlay && isDragging}
      showHandle={showHandle}
      {...attributes}
    >
      {children}
    </Item>
  );
}
